import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Building2, Loader2, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { API_BASE } from "@/lib/api";

const RATE = 999;
const discountFor = (n) => (n >= 200 ? 0.2 : n >= 50 ? 0.1 : 0);

export default function Corporate() {
  const [employees, setEmployees] = useState(15);
  const [company, setCompany] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState(null);

  const est = useMemo(() => {
    const d = discountFor(employees);
    const monthly = employees * RATE * (1 - d);
    return { d, monthly, annual: monthly * 12 };
  }, [employees]);

  const submit = async (e) => {
    e.preventDefault();
    if (!company.trim() || !name.trim()) return toast.error("Please add your company and name.");
    if (!/^\S+@\S+\.\S+$/.test(email)) return toast.error("Please enter a valid work email.");
    setLoading(true);
    try {
      const r = await axios.post(`${API_BASE}/corporate/quote`, { company, name, email, employees });
      setQuote(r.data.quote);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate quote.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0] px-6 py-10">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#5C665A] hover:text-[#2D372B] mb-8">
          <ArrowLeft size={16} /> Back to site
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-full bg-[#89937C]/15 flex items-center justify-center">
            <Building2 size={18} className="text-[#89937C]" />
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-[#2D372B]">Corporate Wellness</h1>
        </div>
        <p className="text-[#5C665A] mb-10">
          Bootcamps, nutrition workshops &amp; wellness coaching for your team. Estimate your investment in seconds.
        </p>

        {quote ? (
          <div data-testid="corporate-quote-result" className="bg-white border border-[#E5E2D9] rounded-3xl p-8 text-center">
            <div className="w-12 h-12 mx-auto rounded-full bg-[#89937C]/15 flex items-center justify-center mb-4">
              <Check size={22} className="text-[#89937C]" />
            </div>
            <h2 className="font-serif-display text-3xl text-[#2D372B] mb-2">Your estimate</h2>
            <div className="font-serif-display text-5xl text-[#2D372B] mt-4">
              ₹{quote.monthly_inr.toLocaleString("en-IN")}<span className="text-lg text-[#5C665A]">/month</span>
            </div>
            <p className="text-sm text-[#5C665A] mt-2">
              {quote.employees} employees · ₹{quote.rate_per_employee}/employee
              {quote.discount_pct > 0 ? ` · ${quote.discount_pct}% volume discount applied` : ""}
            </p>
            <p className="text-sm text-[#5C665A] mt-1">≈ ₹{quote.annual_inr.toLocaleString("en-IN")}/year</p>
            <div className="mt-6 p-4 bg-[#F7F5F0] rounded-2xl text-sm text-[#5C665A]">
              Thanks {name.split(" ")[0]}! Coach Sanjay will email you at <span className="text-[#2D372B] font-medium">{email}</span> within
              24 hours with a tailored proposal for {company}.
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-8">
            <div className="bg-white border border-[#E5E2D9] rounded-3xl p-8">
              <div className="flex items-baseline justify-between mb-6">
                <span className="overline">Team size</span>
                <span data-testid="corporate-employee-count" className="font-serif-display text-4xl text-[#2D372B]">{employees}</span>
              </div>
              <Slider
                data-testid="corporate-employee-slider"
                value={[employees]}
                onValueChange={(v) => setEmployees(v[0])}
                min={5}
                max={50}
                step={5}
              />
              <div className="flex justify-between text-xs text-[#5C665A] mt-2">
                <span>5</span><span>50+</span>
              </div>
              <div className="mt-8 pt-6 border-t border-[#E5E2D9] flex items-baseline justify-between">
                <div>
                  <div className="overline">Estimated Fee</div>
                  <div data-testid="corporate-live-estimate" className="font-serif-display text-4xl text-[#2D372B]">
                    ₹{Math.round(est.monthly).toLocaleString("en-IN")}<span className="text-base text-[#5C665A]">/mo</span>
                  </div>
                  <p data-testid="corporate-rate-note" className="text-xs text-[#5C665A] mt-1">₹999 per member/month · minimum 5 members</p>
                </div>
                {est.d > 0 && (
                  <span className="text-xs px-3 py-1.5 rounded-full bg-[#89937C]/15 text-[#5C665A] font-medium">
                    {est.d * 100}% volume discount
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <div className="overline">Get your detailed proposal</div>
              <Input data-testid="corporate-company-input" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Company name" className="bg-white border-[#E5E2D9] h-12" />
              <Input data-testid="corporate-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="bg-white border-[#E5E2D9] h-12" />
              <Input data-testid="corporate-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Work email" className="bg-white border-[#E5E2D9] h-12" />
            </div>

            <button
              data-testid="corporate-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 bg-[#2D372B] text-[#F7F5F0] font-medium hover:bg-[#89937C] transition-colors disabled:opacity-60"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              Get my quote
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
